import { useQuery } from '@tanstack/react-query';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useService,
} from '../../../../../hooks';
import { DimensionDatum } from './useFetchDimensionData';
import MedicalResultsService, {
  ResultHistory,
} from '../../../../../services/MedicalResultsService';
import { LabResultsConfigItem } from '../useLabResultsConfig';
import { buildResultHistoryFallbackFromV3 } from '../buildResultHistoryFallbackFromV3';
import { formatResultHistoryCodingForDisplay } from '../formatters';

const SERVICE = new MedicalResultsService();

export type UseFetchMedicalResultHistoriesArgs = {
  patientId: string;
  dimensionData?: DimensionDatum[];
  labResultsConfigItem: LabResultsConfigItem;
};

export type MedicalResultHistory = {
  medicalResultHistory: ResultHistory[];
} & DimensionDatum;

export function useFetchMedicalResultHistories({
  patientId,
  dimensionData,
  labResultsConfigItem,
}: UseFetchMedicalResultHistoriesArgs) {
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();
  const [getHistory] = useService(SERVICE.getHistory);

  const code = dimensionData?.[0]?.dimension.codes[0].key;
  const system = dimensionData?.[0]?.dimension.codes[0].system;

  return useQuery({
    queryKey: [
      'medical-results',
      patientId,
      'history',
      { projectId, cohortId, code, system },
      labResultsConfigItem.source,
    ],
    queryFn: async () => {
      return await Promise.all(
        dimensionData!.map(async datum => {
          const response = await getHistory({
            patientId,
            project: projectId,
            code: datum.dimension.codes[0].key,
            system: datum.dimension.codes[0].system,
            cohort: cohortId,
            includeClinicOnly: true,
            source: labResultsConfigItem.source,
          });
          return response.data;
        }),
      );
    },
    enabled: dimensionData !== undefined,
    select: medicalResultHistories => {
      return dimensionData!.map((datum, index) => {
        const fromApi = medicalResultHistories[index].items;
        const fallback = buildResultHistoryFallbackFromV3(datum);
        const items =
          fromApi.length > 0
            ? fromApi
            : fallback.length > 0
            ? fallback
            : fromApi;

        return {
          ...datum,
          medicalResultHistory: formatResultHistoryCodingForDisplay(
            items,
            labResultsConfigItem.source === 'memberUpload',
          ),
        };
      });
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}
